/**
 * Inline event row for MonthView timed events.
 *
 * Renders a single timed event without a chip background — just a small
 * colored dot, the start time, and the title (Google-Calendar style).
 * All-day events keep their chip rendering via `MonthEventChip`; this
 * component is exclusively for timed events whose start time gives the
 * row meaningful context. Past events fade to tertiary text and a
 * desaturated dot.
 */

import {
  allNonOrganizerGuestsDeclined,
  DECLINED_TITLE_CLASSNAME,
} from '@/lib/crm/record/utils/meetingHelpers';
import { EntityTypeIcon } from '@/lib/crm/utils/icons/EntityTypeIcon';
import { useOrgTimeFormat } from '@/lib/time';
import { cx } from '@/utils/cx';

import { getEventColor } from '../utils/calendarColors';
import { formatEventTimeShort } from '../utils/dateUtils';
import type { CalendarEvent } from '../utils/eventMapper';

interface MonthInlineEventRowProps {
  event: CalendarEvent;
  /** IANA timezone (profile timezone) the event's clock time is read in */
  timezone: string;
  onClick?: (event: CalendarEvent) => void;
  /** Whether the event is in the past (faded styling). */
  isPast?: boolean;
  /**
   * When true, the time is hidden on mobile (`md:` and below) — too
   * little horizontal space in the cell to render both. The popover
   * leaves this false because there's room for the full row.
   */
  hideTimeOnMobile?: boolean;
}

/**
 * Compute the dot's CSS color. Past events fade via color-mix; older
 * browsers without color-mix support fall back to a hex-alpha appended
 * to the source color (matches the `MonthEventChip` fallback path).
 */
function getDotBackground(color: string, isPast: boolean): string {
  if (!isPast) {
    return color;
  }
  const supportsColorMix =
    typeof CSS !== 'undefined' &&
    typeof CSS.supports === 'function' &&
    CSS.supports('background-color', 'color-mix(in srgb, red 40%, white)');
  if (supportsColorMix) {
    return `color-mix(in srgb, ${color} 45%, transparent)`;
  }
  // Fallback: hex-alpha (only works for hex colors; harmless for others).
  return `${color}73`; // ~45%
}

export function MonthInlineEventRow({
  event,
  timezone,
  onClick,
  isPast,
  hideTimeOnMobile = false,
}: MonthInlineEventRowProps) {
  const { timeFormat } = useOrgTimeFormat();
  const color = getEventColor(event.color);
  // All-day events have no meaningful clock time — their `start_time`
  // is just `<startDate>T00:00:00`, which would render as a misleading
  // "12am" beside the title. Suppress the time slot entirely.
  const time = event.is_all_day
    ? ''
    : formatEventTimeShort(event.start_time, timeFormat, timezone);
  const dotBackground = getDotBackground(color, isPast || false);
  // De-emphasize meetings where every non-organizer guest declined.
  // See WeekDayColumn for rationale.
  const isAllDeclined = allNonOrganizerGuestsDeclined(event.guests);

  return (
    <button
      type='button'
      onClick={e => {
        e.stopPropagation();
        onClick?.(event);
      }}
      className={cx(
        'group flex w-full items-center gap-1.5 px-1 py-0.5 text-left text-xs',
        'rounded transition-colors hover:bg-secondary cursor-pointer',
        // No focus ring — the row is one of dozens in a calendar cell;
        // a visible focus state reads as a "pressed" stuck-down state
        // after a click. Browser default focus suppressed via
        // `focus:outline-none`.
        'focus:outline-none'
      )}
      title={`${event.title}${time ? ` – ${time}` : ''}`}
    >
      {/* Color dot — sized to the cap-height of the surrounding text. */}
      <span
        aria-hidden
        className='inline-block size-1.5 rounded-full shrink-0'
        style={{ backgroundColor: dotBackground }}
      />

      {time && (
        <span
          className={cx(
            'shrink-0 tabular-nums',
            // Optionally hide on mobile — the in-cell variant of this
            // row uses `hideTimeOnMobile` because month-view cells are
            // too narrow to fit both the time and a useful slice of the
            // title. The popover doesn't set the flag (room for both).
            hideTimeOnMobile && 'hidden md:inline',
            isPast ? 'text-quaternary' : 'text-tertiary'
          )}
        >
          {time}
        </span>
      )}

      {/* Entity-type icon for non-meeting kinds. Meetings get no icon —
          they're the default calendar entity. */}
      {event.entity_type_name !== 'ahoy_meeting' && (
        <EntityTypeIcon
          entityTypeName={event.entity_type_name}
          className={cx(
            'size-3 shrink-0',
            isPast ? 'text-quaternary' : 'text-tertiary'
          )}
        />
      )}

      <span
        className={cx(
          'flex-1 truncate',
          isPast ? 'text-tertiary' : 'text-primary',
          isAllDeclined && DECLINED_TITLE_CLASSNAME
        )}
      >
        {event.title}
      </span>
    </button>
  );
}
